import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const AvailabilityModal = ({ 
  isOpen, 
  onClose, 
  employee, 
  onSave 
}) => {
  const [availability, setAvailability] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const weekDays = [
    { key: 'saturday', label: 'شنبه', shortLabel: 'ش' },
    { key: 'sunday', label: 'یکشنبه', shortLabel: 'ی' },
    { key: 'monday', label: 'دوشنبه', shortLabel: 'د' },
    { key: 'tuesday', label: 'سه‌شنبه', shortLabel: 'س' },
    { key: 'wednesday', label: 'چهارشنبه', shortLabel: 'چ' },
    { key: 'thursday', label: 'پنج‌شنبه', shortLabel: 'پ' },
    { key: 'friday', label: 'جمعه', shortLabel: 'ج' }
  ];

  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00'
  ];

  useEffect(() => {
    if (employee && isOpen) {
      // Initialize availability data
      const initialAvailability = {};
      weekDays?.forEach(day => {
        initialAvailability[day.key] = {
          isWorking: employee?.workingDays?.includes(day?.key) || false,
          startTime: employee?.workingHours?.start || '09:00',
          endTime: employee?.workingHours?.end || '18:00',
          breakStart: '12:30',
          breakEnd: '13:30',
          hasBreak: true,
          customSlots: []
        };
      });
      setAvailability(initialAvailability);
    }
  }, [employee, isOpen]);

  const handleDayToggle = (dayKey, isWorking) => {
    setAvailability(prev => ({
      ...prev,
      [dayKey]: {
        ...prev?.[dayKey],
        isWorking
      }
    }));
  };

  const handleTimeChange = (dayKey, field, value) => {
    setAvailability(prev => ({
      ...prev,
      [dayKey]: {
        ...prev?.[dayKey],
        [field]: value
      }
    }));
  };

  const handleBreakToggle = (dayKey, hasBreak) => {
    setAvailability(prev => ({
      ...prev,
      [dayKey]: {
        ...prev?.[dayKey],
        hasBreak
      }
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await onSave(employee?.id, availability);
      onClose();
    } catch (error) {
      console.error('Error saving availability:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyToAllDays = (sourceDay) => {
    const sourceData = availability?.[sourceDay];
    const newAvailability = { ...availability };
    
    weekDays?.forEach(day => {
      if (day?.key !== sourceDay && newAvailability?.[day?.key]?.isWorking) {
        newAvailability[day.key] = {
          ...newAvailability?.[day?.key],
          startTime: sourceData?.startTime,
          endTime: sourceData?.endTime,
          breakStart: sourceData?.breakStart,
          breakEnd: sourceData?.breakEnd,
          hasBreak: sourceData?.hasBreak
        };
      }
    });
    
    setAvailability(newAvailability);
  };

  if (!isOpen || !employee) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-modal z-modal flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              مدیریت دسترسی - {employee?.name}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              تنظیم ساعات کاری و زمان‌های در دسترس
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          >
            <span className="sr-only">بستن</span>
          </Button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Quick Actions */}
            <div className="bg-muted rounded-lg p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">عملیات سریع</h3>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newAvailability = { ...availability };
                    weekDays?.forEach(day => {
                      newAvailability[day.key] = {
                        ...newAvailability?.[day?.key],
                        isWorking: true
                      };
                    });
                    setAvailability(newAvailability);
                  }}
                >
                  فعال کردن همه روزها
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newAvailability = { ...availability };
                    weekDays?.forEach(day => {
                      if (day?.key !== 'friday') {
                        newAvailability[day.key] = {
                          ...newAvailability?.[day?.key],
                          isWorking: true
                        };
                      }
                    });
                    setAvailability(newAvailability);
                  }}
                >
                  روزهای کاری (بدون جمعه)
                </Button>
              </div>
            </div>

            {/* Weekly Schedule */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground">برنامه هفتگی</h3>
              
              <div className="space-y-4">
                {weekDays?.map((day) => {
                  const dayData = availability?.[day?.key] || {};
                  
                  return (
                    <div key={day?.key} className="bg-muted rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={dayData?.isWorking || false}
                            onChange={(e) => handleDayToggle(day?.key, e?.target?.checked)}
                          />
                          <span className="text-sm font-medium text-foreground">
                            {day?.label}
                          </span>
                        </div>
                        
                        {dayData?.isWorking && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => applyToAllDays(day?.key)}
                            iconName="Copy"
                            iconSize={14}
                          >
                            اعمال به همه
                          </Button>
                        )}
                      </div>
                      {dayData?.isWorking && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in">
                          <Input
                            label="شروع کار"
                            type="time"
                            value={dayData?.startTime || '09:00'}
                            onChange={(e) => handleTimeChange(day?.key, 'startTime', e?.target?.value)}
                          />
                          
                          <Input
                            label="پایان کار"
                            type="time"
                            value={dayData?.endTime || '18:00'}
                            onChange={(e) => handleTimeChange(day?.key, 'endTime', e?.target?.value)}
                          />

                          <div className="space-y-2">
                            <Checkbox
                              label="زمان استراحت"
                              checked={dayData?.hasBreak || false}
                              onChange={(e) => handleBreakToggle(day?.key, e?.target?.checked)}
                            />
                            
                            {dayData?.hasBreak && (
                              <div className="grid grid-cols-2 gap-2">
                                <Input
                                  type="time"
                                  value={dayData?.breakStart || '12:30'}
                                  onChange={(e) => handleTimeChange(day?.key, 'breakStart', e?.target?.value)}
                                  className="text-xs"
                                />
                                <Input
                                  type="time"
                                  value={dayData?.breakEnd || '13:30'}
                                  onChange={(e) => handleTimeChange(day?.key, 'breakEnd', e?.target?.value)}
                                  className="text-xs"
                                />
                              </div>
                            )}
                          </div>

                          <div className="flex items-center">
                            <span className="text-xs text-muted-foreground">
                              {dayData?.startTime && dayData?.endTime && (
                                `${((new Date(`2000-01-01T${dayData.endTime}`) - new Date(`2000-01-01T${dayData.startTime}`)) / (1000 * 60 * 60))?.toFixed(1)} ساعت`
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Summary */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <h3 className="text-sm font-medium text-foreground mb-2">خلاصه برنامه</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">روزهای کاری: </span>
                  <span className="font-medium text-foreground">
                    {Object.values(availability)?.filter(day => day?.isWorking)?.length} روز
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">میانگین ساعات: </span>
                  <span className="font-medium text-foreground">
                    {(Object.values(availability)?.filter(day => day?.isWorking)?.reduce((acc, day) => {
                        if (day?.startTime && day?.endTime) {
                          const hours = (new Date(`2000-01-01T${day.endTime}`) - new Date(`2000-01-01T${day.startTime}`)) / (1000 * 60 * 60);
                          return acc + hours;
                        }
                        return acc;
                      }, 0) / Math.max(Object.values(availability)?.filter(day => day?.isWorking)?.length, 1))?.toFixed(1)} ساعت
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            انصراف
          </Button>
          <Button
            onClick={handleSave}
            loading={isLoading}
            iconName="Save"
            iconPosition="left"
          >
            ذخیره تنظیمات
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityModal;